/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { PlainMarkdown } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    EmptyState,
    EmptyStateErrorHeader,
    EmptyStateIcon,
    Heading,
    Pill,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { AISearchAnswerSourcesSection } from './AISearchAnswerSourcesSection';
import { AISearchAnswerToolbar } from './AISearchAnswerToolbar';
import type { AISearchQueryState } from './useAISearchQuery';
import { useStreamAskInOrganization } from './useStreamAskInOrganization';

type AISearchAnswerProps = {
    query: AISearchQueryState;
    target: { organization: string };
    onAnswerLoaded?: () => void;
    onSubmitQuery: (query: string) => void;
};

/**
 * Fetch the render to a question using the AI backend.
 */
export function AISearchAnswer(props: AISearchAnswerProps): React.ReactElement {
    const { query } = props;

    const onError = useEventCallback(() => {
        analytics.logEvent({
            type: 'ai_search_crash',
            query: query.query,
        });
    });

    return (
        <Sentry.ErrorBoundary onError={onError} fallback={() => <AISearchErrorFallback />}>
            <React.Suspense fallback={<AISearchAnswerPlaceholder />}>
                <AISearchAnswerContent {...props} />
            </React.Suspense>
        </Sentry.ErrorBoundary>
    );
}

function AISearchAnswerContent(props: AISearchAnswerProps) {
    const { target, query, onSubmitQuery } = props;

    const [stream, streamActive] = useStreamAskInOrganization(target, { query: query.query });

    const [streamStarted, setStreamStarted] = React.useState(false);
    const streamingIsFinished = streamStarted && !streamActive;
    React.useEffect(() => {
        if (streamActive) {
            setStreamStarted(true);
        }
    }, [streamActive]);

    // sometimes the stream length is 0 and because there is no answer. We need to check whether
    // it is zero and we've ended a stream that was started
    const loading = stream.length === 0 && !streamingIsFinished;
    const answer = stream[stream.length - 1];

    const logViewAnswerEvent = useEventCallback(() => {
        const event = {
            type: 'ai_search_view_answer' as const,
            query: query.query,
            queriesInMemory: query.previousQueries.length,
            hasAnswer: answer?.answer !== undefined,
            hasFollowupQuestions: (answer?.followupQuestions.length ?? 0) > 0,
        };

        analytics.logEvent(event);
    });

    React.useEffect(() => {
        if (!streamActive) {
            logViewAnswerEvent();
        }
    }, [streamActive, logViewAnswerEvent]);

    const onAnswerLoaded = useEventCallback(props.onAnswerLoaded ?? (() => {}));
    const hasAnswer = answer?.answer !== undefined;
    React.useEffect(() => {
        if (hasAnswer) {
            onAnswerLoaded();
        }
    }, [hasAnswer, onAnswerLoaded]);

    if (loading && !answer) {
        return <AISearchAnswerPlaceholder />;
    }

    const onPressFollowupQuestion = (followup: string) => {
        analytics.logEvent({
            type: 'ai_search_pick_followup',
            query: query.query,
            followup,
            previousQueriesCount: query.previousQueries.length,
        });

        onSubmitQuery(followup);
    };

    const followupQuestions = answer?.followupQuestions?.length ? (
        <div className="flex flex-col border-t pt-4">
            <h3 className="text-small my-4 text-muted">Related queries</h3>
            <div className="flex flex-wrap content-center items-center gap-4 self-stretch">
                {answer?.followupQuestions.map((question) => (
                    <Pill
                        key={question}
                        onPress={() => {
                            setStreamStarted(false);
                            onPressFollowupQuestion(question);
                        }}
                        leadingIcon={<Icon.SearchOrAsk className="shrink-0 text-primary-low" />}
                        label={question}
                    />
                ))}
            </div>
        </div>
    ) : null;

    if (!hasAnswer) {
        return (
            <AISearchAnswerNotFound organizationKey={target.organization}>
                <>
                    {answer?.sources ? <AISearchAnswerSourcesSection answer={answer} /> : null}
                    {followupQuestions}
                </>
            </AISearchAnswerNotFound>
        );
    }

    return (
        <div className="flex flex-col gap-5 p-5 pt-4 select-text">
            {/* The question is still displayed in the input field where the user asked it, so we don't repeat it here. */}

            {/* Answer */}
            {'document' in answer && (
                <div className="text-ui-small text-neutral-high">
                    <PlainMarkdown input={answer.document} style={{ minWidth: 0 }} />
                </div>
            )}

            {/* Buttons */}
            {hasAnswer ? (
                <div className="flex items-center gap-4 border-b pb-4">
                    <AISearchAnswerToolbar query={query.query} answer={answer} size="medium" />
                </div>
            ) : null}

            {/* Sources */}
            <AISearchAnswerSourcesSection answer={answer} />

            {followupQuestions}
        </div>
    );
}

function AISearchAnswerPlaceholder(): React.ReactElement {
    const [delayed, setDelayed] = React.useState(false);

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setDelayed(true);
        }, 3000);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.AIAgentAnimated state="loading" size={16} className="text-neutral-low" />
            </EmptyStateIcon>
            <Heading>Analysing the content to answer your question…</Heading>
            <Description className={clsx('transition', delayed ? 'opacity-100' : 'opacity-0')}>
                It might take a few more seconds.
            </Description>
        </EmptyState>
    );
}

function AISearchAnswerNotFound(props: {
    organizationKey?: string;
    children: React.ReactElement;
}): React.ReactElement {
    const { children } = props;

    return (
        <div className="flex w-full flex-col items-start justify-start gap-2 rounded-lg p-6">
            <h2 className="text-ui-heading-small">
                GitBook AI couldn’t answer the question with the information it has. You could try
                rephrasing it, or be more specific.
            </h2>
            {children}
        </div>
    );
}

function AISearchErrorFallback() {
    return (
        <EmptyState>
            <EmptyStateErrorHeader />
            <Description>Please try again later.</Description>
        </EmptyState>
    );
}
